import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
  CircularProgress
} from '@mui/material';
import { registerUser, loginWithGoogle } from '../services/authService';
import { UserRole } from '../utils/types';
import GoogleIcon from '@mui/icons-material/Google';

const RegisterUserPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password || !displayName) {
      setError('يرجى إدخال جميع الحقول المطلوبة');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // تعيين دور العميل تلقائيًا
      await registerUser(email, password, displayName, UserRole.CUSTOMER);
      setSuccess(true);

      // بعد 3 ثوانٍ، انتقل إلى صفحة تسجيل الدخول
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      console.error('Error during registration:', err);

      // التعامل مع أخطاء Firebase
      if (err.code === 'auth/email-already-in-use') {
        setError('البريد الإلكتروني مستخدم بالفعل');
      } else if (err.code === 'auth/weak-password') {
        setError('كلمة المرور ضعيفة جدًا');
      } else if (err.code === 'auth/invalid-email') {
        setError('البريد الإلكتروني غير صالح');
      } else {
        setError('حدث خطأ أثناء التسجيل. يرجى المحاولة مرة أخرى');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setGoogleLoading(true);
      setError(null);

      await loginWithGoogle();
      // بعد تسجيل الدخول بنجاح، انتقل إلى الصفحة الرئيسية
      navigate('/');
    } catch (err: any) {
      console.error('Error during Google sign in:', err);

      // التعامل مع أخطاء Firebase المحددة
      if (err.code === 'auth/operation-not-allowed') {
        setError('المصادقة باستخدام جوجل غير مفعلة في النظام. يرجى التواصل مع مدير النظام لتفعيلها.');
        console.error('يجب تفعيل مزود المصادقة باستخدام جوجل في لوحة تحكم Firebase');
      } else if (err.code === 'auth/popup-closed-by-user') {
        setError('تم إغلاق نافذة تسجيل الدخول بواسطة المستخدم');
      } else if (err.code === 'auth/cancelled-popup-request') {
        setError('تم إلغاء طلب النافذة المنبثقة');
      } else if (err.code === 'auth/popup-blocked') {
        setError('تم حظر النافذة المنبثقة. يرجى السماح بالنوافذ المنبثقة لهذا الموقع');
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('حدث خطأ أثناء تسجيل الدخول باستخدام جوجل');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  if (success) {
    return (
      <Container maxWidth="sm">
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              width: '100%',
              borderRadius: 2,
              textAlign: 'center'
            }}
          >
            <Alert severity="success" sx={{ mb: 2 }}>
              تم إنشاء المستخدم بنجاح! سيتم توجيهك إلى صفحة تسجيل الدخول...
            </Alert>
            <CircularProgress sx={{ mt: 2 }} />
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            borderRadius: 2,
            textAlign: 'center'
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom>
            نظام إدارة مركز الصيانة
          </Typography>

          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            إنشاء مستخدم جديد
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              label="الاسم الكامل"
              variant="outlined"
              fullWidth
              required
              margin="normal"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoComplete="name"
            />

            <TextField
              label="البريد الإلكتروني"
              type="email"
              variant="outlined"
              fullWidth
              required
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />

            <TextField
              label="كلمة المرور"
              type="password"
              variant="outlined"
              fullWidth
              required
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? 'جارِ التسجيل...' : 'تسجيل حساب جديد'}
            </Button>

            <Divider sx={{ my: 2 }}>أو</Divider>

            <Button
              variant="contained"
              color="info"
              fullWidth
              size="large"
              startIcon={<GoogleIcon />}
              onClick={handleGoogleSignIn}
              disabled={googleLoading}
              sx={{ mb: 2 }}
            >
              {googleLoading ? 'جارِ التسجيل...' : 'التسجيل باستخدام جوجل'}
            </Button>

            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              size="large"
              onClick={() => navigate('/login')}
            >
              العودة إلى تسجيل الدخول
            </Button>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
              بالتسجيل، أنت توافق على شروط الاستخدام وسياسة الخصوصية
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default RegisterUserPage;